'use client';

import { useCallback } from 'react';
import config from 'config';
import { defaultBasketData, ROUTES } from 'constant';
import { useRouter } from 'next/navigation';
import { useGetPackagesQuery } from 'services/packagesApi';
import {
  useAddToShoppingCartMutation,
  useGetShoppingCartQuery,
  useRemoveGiftCardPaymentMutation,
  useUpdateShoppingCartItemMutation
} from 'services/shoppingCartApi';

import { Box, Flex, Grid, GridItem, Text, useTheme } from '@chakra-ui/react';

import { PackageInterface, PackageListType, ShopCartType } from 'types';

import { ContentLoader } from 'components/ContentLoader';
import { Loader } from 'ui';
import { PackageItem } from '../PackageItem';

const {
  myPackages: { emptyMessage }
} = config;

const PackagesList = () => {
  const {
    colors: {
      palette: {
        dashboard: {
          packages: { packagesList }
        }
      }
    }
  } = useTheme();
  const router = useRouter();
  const { data: packages, isLoading } = useGetPackagesQuery(
    PackageListType.ACTIVE
  );
  const { data = defaultBasketData } = useGetShoppingCartQuery('');
  const [addToShoppingCart, { isLoading: isAdding }] =
    useAddToShoppingCartMutation();
  const [updateShoppingCartItem, { isLoading: isUpdating }] =
    useUpdateShoppingCartItemMutation();
  const [removeGiftCardPayment] = useRemoveGiftCardPaymentMutation();

  const redirectToCart = useCallback(() => router.push(ROUTES.cart), [router]);

  const removeGiftCardPaymentHandler = useCallback(() => {
    if (data.payments.length > 0) {
      const pymtIds = data.payments.map((payment) => payment?.in_pymt_id);
      const deletePromises = pymtIds.map((id) =>
        removeGiftCardPayment({
          in_pymt_id: id,
          invoice_id: data.details.invoice_id ?? 0
        })
      );

      return Promise.allSettled(deletePromises);
    }
  }, [data.payments, data.details.invoice_id, removeGiftCardPayment]);

  const handleItemBuy = useCallback(
    async (pack: PackageInterface) => {
      const existPack = data.items.find((item) => item.id === pack.tmpl_id);
      if (existPack?.id) {
        await updateShoppingCartItem({
          invoiceId: data.details.invoice_id ?? 0,
          cartItemId: existPack?.cart_item_id ?? 0,
          itemId: existPack?.id ?? 0,
          itemType: existPack?.item_type ?? ShopCartType.Package,
          qty: existPack.qty + 1
        })
          .then(redirectToCart)
          .then(removeGiftCardPaymentHandler);
      } else {
        await addToShoppingCart({
          item_id: pack.tmpl_id,
          item_type: ShopCartType.Package,
          store_id: pack.store_id,
          qty: 1
        })
          .then(redirectToCart)
          .then(removeGiftCardPaymentHandler);
      }
    },
    [
      redirectToCart,
      data.items,
      data.details.invoice_id,
      addToShoppingCart,
      removeGiftCardPaymentHandler,
      updateShoppingCartItem
    ]
  );

  const isItemsExist = packages?.length;

  return (
    <Box
      bg={packagesList.containerBg}
      mx={{ base: '-15px', sm: 0 }}
      borderRadius={{ base: 0, sm: 6 }}
      alignSelf='stretch'
      overflow='hidden'
      position='relative'
    >
      <Grid
        px={{ base: '15px', md: '20px' }}
        py='9px'
        bg={packagesList.headerBg}
      >
        <GridItem>
          <Flex h='100%' alignItems='center' justifyContent='start'>
            <Text
              fontSize='13px'
              lineHeight='140%'
              textTransform='uppercase'
              color={packagesList.headerTextColor}
            >
              Active Packages
            </Text>
          </Flex>
        </GridItem>
      </Grid>
      {(isAdding || isUpdating) && <Loader absolute bg='blackOpacity.10' />}
      {isItemsExist ? (
        <Box w='100%'>
          <Grid
            templateColumns={{
              base: 'repeat(7, 1fr)',
              lg: 'repeat(24, 1fr)'
            }}
            px={{ base: '15px', md: '20px' }}
            pt='12px'
            pb='11px'
            columnGap='20px'
            bg={packagesList.containerBg}
            borderBottom='1px solid'
            borderBottomColor={packagesList.borderBottomColor}
          >
            <GridItem colSpan={{ base: 5, lg: 8 }}>
              <Flex h='100%' alignItems='center' justifyContent='start'>
                <Text
                  fontSize='12px'
                  color={packagesList.subHeaderTextColor}
                  textTransform='uppercase'
                  lineHeight='140%'
                >
                  Description
                </Text>
              </Flex>
            </GridItem>
            <GridItem colSpan={{ base: 2, lg: 3 }}>
              <Flex
                h='100%'
                alignItems='center'
                justifyContent='center'
                px='15px'
              >
                <Text
                  fontSize='12px'
                  color={packagesList.subHeaderTextColor}
                  textTransform='uppercase'
                  lineHeight='140%'
                >
                  Units
                </Text>
              </Flex>
            </GridItem>

            <GridItem display={{ base: 'none', lg: 'block' }} colSpan={4}>
              <Flex
                h='100%'
                alignItems='center'
                justifyContent='start'
                paddingLeft={{ base: 0, lg: '25%' }}
              >
                <Text
                  fontSize='12px'
                  color={packagesList.subHeaderTextColor}
                  textTransform='uppercase'
                  lineHeight='140%'
                >
                  Expires
                </Text>
              </Flex>
            </GridItem>
            <GridItem display={{ base: 'none', lg: 'block' }} colSpan={4}>
              <Flex h='100%' alignItems='center' justifyContent='end'>
                <Text
                  fontSize='12px'
                  color={packagesList.subHeaderTextColor}
                  textTransform='uppercase'
                  lineHeight='140%'
                >
                  Amount
                </Text>
              </Flex>
            </GridItem>
          </Grid>
          {packages.map((packageItem, index) => (
            <PackageItem
              key={packageItem.id}
              item={packageItem}
              isLastItem={index === packages.length - 1}
              onBuyHandler={() => handleItemBuy(packageItem)}
            />
          ))}
        </Box>
      ) : (
        <ContentLoader
          bg={packagesList.containerBg}
          isLoading={isLoading}
          label={emptyMessage}
          minHeight={{ base: '80px', md: '150px' }}
        />
      )}
    </Box>
  );
};

export default PackagesList;
